<template>
  <div>
    <base-page-heading title="Work Orders">
      <template #extra>
        <b-button
          v-if="$can('create', 'WorkOrder')"
          variant="primary"
          @click="() => $router.push({ name: 'new-work-order' })"
          v-b-tooltip.hover
          title="Create a new Work Order"
        >
          <i class="fa fa-plus" />
          <span class="ml-1 d-none d-md-inline">New Work Order</span>
        </b-button>

        <b-dropdown
          v-if="$can('create', 'Import')"
          text="Import"
          variant="outline-secondary"
          right
          v-b-tooltip.hover
          title="Manage Work Orders using imports"
        >
          <template #button-content>
            <i class="fa fa-file-excel" />
            <span class="ml-1 d-none d-md-inline">Import</span>
          </template>
          <b-dropdown-group>
            <b-dropdown-item v-b-modal.work-orders-imports-add-modal>
              New
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.work-order-charges-imports-add-modal>
              New Charges
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.update-work-orders-imports-add-modal>
              Update
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.update-work-order-charges-imports-add-modal>
              Update Charges
            </b-dropdown-item>

            <b-dropdown-item v-b-modal.assign-work-orders-imports-add-modal>
              Assign
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.check-out-work-orders-imports-add-modal>
              Check Out
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.cancel-work-orders-imports-add-modal>
              Cancel
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.undo-work-orders-completion-imports-add-modal>
              Undo Completion
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.reopen-work-orders-imports-add-modal>
              Reopen Cancelled
            </b-dropdown-item>
            <b-dropdown-divider />
          </b-dropdown-group>

          <b-dropdown-group header="3rd Party Integrations">
            <b-dropdown-item v-b-modal.integrations-work-orders-imports-add-modal>
              Edit Integration Data
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.officetrax-work-orders-photos-push-imports-add-modal>
              Push Photos to Officetrax
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.officetrax-work-orders-completions-push-imports-add-modal>
              Push Completions to Officetrax
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.service-channel-work-orders-photos-push-imports-add-modal>
              Push Photos to ServiceChannel
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.service-channel-work-orders-push-imports-add-modal>
              Push Completions to ServiceChannel
            </b-dropdown-item>
          </b-dropdown-group>
        </b-dropdown>

        <b-dropdown
          variant="outline-secondary"
          right
          v-b-tooltip.hover
          title="Download spreadsheet"
        >
          <template #button-content>
            <b-spinner
              v-if="downloading"
              small
              label="Downloading..."
              class="mr-1"
            />
            <i
              v-else
              class="fa fa-download mr-1"
            />
            <span class="d-none d-md-inline">Downloads</span>
          </template>
          <b-dropdown-item @click="downloadSpreadsheet">
            Work Orders
          </b-dropdown-item>
          <b-dropdown-item @click="downloadChargesSpreadsheet">
            Charges
          </b-dropdown-item>
          <b-dropdown-item
            v-if="$store.getters.isCMSOrganization"
            @click="downloadCMSOpsSpreadsheet"
          >
            CMS Ops Export
          </b-dropdown-item>

          <b-dropdown-group>
            <b-dropdown-item v-b-modal.work-order-revenue-accrual-report-modal>
              Revenue Accrual
            </b-dropdown-item>
            <b-dropdown-item v-b-modal.work-order-cost-accrual-report-modal>
              Cost Accrual
            </b-dropdown-item>
          </b-dropdown-group>
          <b-dropdown-item
            @click="downloadAgedFirstCallReport"
            v-if="$store.getters.isScopesOrganization"
          >
            DG Aged 1st Call
          </b-dropdown-item>

          <b-dropdown-divider />

          <b-dropdown-item v-b-modal.work-order-email-multiple-modal>
            Email Work Order PDFs
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>
    <div class="content">
      <b-row>
        <b-col>
          <work-orders-list-filter
            :columns="columns"
            :initial-filters="filters"
            :total-rows="totalRows"
            @filters-applied="applyFilters"
          />
        </b-col>
      </b-row>

      <b-row
        v-if="hasSelectedWorkOrders"
        class="mb-2"
      >
        <b-col class="d-flex align-items-center">
          <b-dropdown
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Bulk actions for selected Work Orders"
          >
            <template #button-content>
              Bulk Actions
              <span
                class="ml-1"
              >
                ({{ selectedWorkOrders.length }})
              </span>
            </template>

            <b-dropdown-item @click="openSelectedEmailModal">
              <i class="fa fa-envelope mr-1" />
              Email PDFs
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('assign', 'WorkOrder')"
              @click="openSelectedAssignModal"
            >
              <i class="fa fa-user mr-1" />
              Assign
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('complete', 'WorkOrder')"
              @click="openSelectedCompleteModal"
            >
              <i class="fa fa-check mr-1" />
              Complete
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('cancel', 'WorkOrder')"
              @click="openSelectedCancelModal"
            >
              <i class="fa fa-ban mr-1" />
              Cancel
            </b-dropdown-item>
            <b-dropdown-item
              v-if="$can('undo_completion', 'WorkOrder')"
              @click="openSelectedUndoCompletionModal"
            >
              <i class="fa fa-undo mr-1" />
              Undo Check-In / Out
            </b-dropdown-item>
          </b-dropdown>

          <b-button
            class="ml-2"
            variant="link-dark"
            @click="clearSelectedWorkOrders"
          >
            Clear
          </b-button>
        </b-col>
      </b-row>

      <work-orders-table
        ref="workOrdersTable"
        :filters="filters"
        :columns="columns"
        :current-page="pagination.currentPage"
        :per-page="pagination.perPage"
        @selected-changed="onSelectedWorkOrdersChanged"
      />
    </div>

    <!-- Page Modals -->
    <work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <work-order-charges-import-new-modal v-if="$can('create', 'Import')" />
    <assign-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <check-out-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <cancel-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <update-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <update-work-order-charges-import-new-modal v-if="$can('create', 'Import')" />
    <undo-work-orders-completion-import-new-modal v-if="$can('create', 'Import')" />
    <reopen-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <integrations-work-orders-import-new-modal v-if="$can('create', 'Import')" />
    <service-channel-work-orders-push-import-new-modal v-if="$can('create', 'Import')" />
    <service-channel-work-orders-photos-push-import-new-modal v-if="$can('create', 'Import')" />
    <officetrax-work-orders-photos-push-import-new-modal v-if="$can('create', 'Import')" />
    <officetrax-work-orders-completions-push-import-new-modal v-if="$can('create', 'Import')" />
    <work-orders-email-multiple-modal :filters="filters" />
    <work-orders-revenue-accrual-report-modal />
    <work-orders-cost-accrual-report-modal />
    <work-orders-email-modal
      :filters="filters"
      :work-orders="selectedWorkOrders"
      @email-sent="clearSelectedWorkOrders"
    />
    <assign-modal
      v-if="$can('assign', 'WorkOrder')"
      :work-orders="selectedWorkOrders"
      @assigned="clearSelectedWorkOrders"
    />
    <complete-modal
      v-if="$can('complete', 'WorkOrder')"
      :work-orders="selectedWorkOrders"
      @completed="clearSelectedWorkOrders"
    />
    <cancel-modal
      v-if="$can('cancel', 'WorkOrder')"
      :work-orders="selectedWorkOrders"
      @cancelled="clearSelectedWorkOrders"
    />
    <undo-completion-modal
      v-if="$can('undo_completion', 'WorkOrder')"
      :work-orders="selectedWorkOrders"
      @undone="clearSelectedWorkOrders"
    />
  </div>
</template>

<script>
import listMixin from '@/mixins/listMixin';
import downloadMixin from '@/mixins/downloadMixin';
import { WORK_ORDER_TABLE_NAME } from '@/constants/workOrders';
import WorkOrdersImportNewModal from '@/components/admin/imports/WorkOrdersNewModal.vue';
import WorkOrderChargesImportNewModal from '@/components/admin/imports/WorkOrderChargesNewModal.vue';
import AssignWorkOrdersImportNewModal from '@/components/admin/imports/AssignWorkOrdersNewModal.vue';
import CheckOutWorkOrdersImportNewModal from '@/components/admin/imports/CheckOutWorkOrdersNewModal.vue';
import CancelWorkOrdersImportNewModal from '@/components/admin/imports/CancelWorkOrdersNewModal.vue';
import UpdateWorkOrdersImportNewModal from '@/components/admin/imports/UpdateWorkOrdersNewModal.vue';
import UpdateWorkOrderChargesImportNewModal from '@/components/admin/imports/UpdateWorkOrderChargesNewModal.vue';
import UndoWorkOrdersCompletionImportNewModal from '@/components/admin/imports/UndoWorkOrdersCompletionNewModal.vue';
import ReopenWorkOrdersImportNewModal from '@/components/admin/imports/ReopenWorkOrdersNewModal.vue';
import IntegrationsWorkOrdersImportNewModal from '@/components/admin/imports/IntegrationsWorkOrdersNewModal.vue';
import OfficetraxWorkOrdersPhotosPushImportNewModal from '@/components/admin/imports/OfficetraxWorkOrdersPhotosPushNewModal.vue';
import OfficetraxWorkOrdersCompletionsPushImportNewModal from '@/components/admin/imports/OfficetraxWorkOrdersCompletionsPushNewModal.vue';
import ServiceChannelWorkOrdersPushImportNewModal from '@/components/admin/imports/ServiceChannelWorkOrdersPushNewModal.vue';
import ServiceChannelWorkOrdersPhotosPushImportNewModal from '@/components/admin/imports/ServiceChannelWorkOrdersPhotosPushNewModal.vue';
import WorkOrdersListFilter from '@/components/admin/workOrders/ListFilter.vue';
import WorkOrdersTable from '@/components/admin/workOrders/Table.vue';
import WorkOrdersEmailModal from '@/components/admin/workOrders/EmailModal.vue';
import AssignModal from '@/components/admin/workOrders/AssignModal.vue';
import CompleteModal from '@/components/admin/workOrders/CompleteModal.vue';
import CancelModal from '@/components/admin/workOrders/CancelModal.vue';
import UndoCompletionModal from '@/components/admin/workOrders/UndoCompletionModal.vue';
import WorkOrdersEmailMultipleModal from '@/components/admin/workOrders/EmailMultipleModal.vue';
import WorkOrdersRevenueAccrualReportModal from '@/components/admin/workOrders/RevenueAccrualReportModal.vue';
import WorkOrdersCostAccrualReportModal from '@/components/admin/workOrders/CostAccrualReportModal.vue';

export default {
  name: 'WorkOrdersList',
  components: {
    WorkOrdersImportNewModal,
    WorkOrderChargesImportNewModal,
    AssignWorkOrdersImportNewModal,
    CheckOutWorkOrdersImportNewModal,
    CancelWorkOrdersImportNewModal,
    UpdateWorkOrdersImportNewModal,
    UpdateWorkOrderChargesImportNewModal,
    UndoWorkOrdersCompletionImportNewModal,
    ReopenWorkOrdersImportNewModal,
    IntegrationsWorkOrdersImportNewModal,
    OfficetraxWorkOrdersPhotosPushImportNewModal,
    OfficetraxWorkOrdersCompletionsPushImportNewModal,
    ServiceChannelWorkOrdersPushImportNewModal,
    ServiceChannelWorkOrdersPhotosPushImportNewModal,
    WorkOrdersTable,
    WorkOrdersListFilter,
    WorkOrdersEmailModal,
    AssignModal,
    CompleteModal,
    CancelModal,
    UndoCompletionModal,
    WorkOrdersEmailMultipleModal,
    WorkOrdersRevenueAccrualReportModal,
    WorkOrdersCostAccrualReportModal,
  },
  mixins: [listMixin, downloadMixin],
  props: {
    text: String,
    number: String,
    client_id: String,
    client_ids: Array,
    location_id: String,
    service_id: String,
    manager_id: String,
    client_manager_id: String,
    parent_id: String,
    city: String,
    county: String,
    states: Array,
    description: String,
    date_bucket_start_from: String,
    date_bucket_start_to: String,
    date_bucket_end_from: String,
    date_bucket_end_to: String,
    checked_in_from: String,
    checked_in_to: String,
    checked_out_from: String,
    checked_out_to: String,
    invoice_date_from: String,
    invoice_date_to: String,
    assigned_to_id: String,
    done_by_id: String,
    statuses: Array,
    sub_status: String,
    invoiced: String,
    technician_paid: String,
    approved: String,
    completion_sources: Array,
    tags: Array,
    check_in_distance: String,
    no_service: String,
    capex: String,
  },
  data() {
    return {
      tableName: WORK_ORDER_TABLE_NAME,
      filters: {
        text: this.text,
        number: this.number,
        client_ids: this.initialClientIds(),
        location_id: this.location_id,
        service_id: this.service_id,
        manager_id: this.manager_id,
        client_manager_id: this.client_manager_id,
        parent_id: this.parent_id,
        city: this.city,
        county: this.county,
        states: this.states,
        description: this.description,
        date_bucket_start_from: this.date_bucket_start_from,
        date_bucket_start_to: this.date_bucket_start_to,
        date_bucket_end_from: this.date_bucket_end_from,
        date_bucket_end_to: this.date_bucket_end_to,
        checked_in_from: this.checked_in_from,
        checked_in_to: this.checked_in_to,
        checked_out_from: this.checked_out_from,
        checked_out_to: this.checked_out_to,
        invoice_date_from: this.invoice_date_from,
        invoice_date_to: this.invoice_date_to,
        assigned_to_id: this.assigned_to_id,
        done_by_id: this.done_by_id,
        statuses: this.statuses,
        sub_status: this.sub_status,
        invoiced: this.invoiced,
        technician_paid: this.technician_paid,
        approved: this.approved,
        no_service: this.no_service,
        capex: this.capex,
        completion_sources: this.completion_sources,
        tags: this.tags,
        check_in_distance: this.check_in_distance,
      },
      selectedWorkOrders: [],
    };
  },
  methods: {
    initialClientIds() {
      if (Array.isArray(this.client_ids) && this.client_ids.length) {
        return this.client_ids.map((id) => String(id));
      }
      if (this.client_id) {
        return [String(this.client_id)];
      }
      const storeClientId = this.$store.getters?.client?.id;
      return storeClientId ? [String(storeClientId)] : undefined;
    },
    downloadSpreadsheet() {
      this.download(this.$workOrdersAPI.getSpreadsheet, this.filters, 'Work Orders.csv');
    },
    downloadCMSOpsSpreadsheet() {
      this.download(this.$workOrdersAPI.getSpreadsheet, { ...this.filters, cms_report: true }, 'Work Orders.csv');
    },
    downloadChargesSpreadsheet() {
      this.download(this.$workOrderChargesAPI.getSpreadsheet, this.filters, 'Charges.csv');
    },
    downloadAgedFirstCallReport() {
      this.download(this.$workOrdersAPI.getAgedFirstCallReport, this.filters, 'Aged 1st Call.xlsx');
    },
    onSelectedWorkOrdersChanged(workOrders) {
      this.selectedWorkOrders = workOrders || [];
    },
    openSelectedEmailModal() {
      if (!this.hasSelectedWorkOrders) {
        return;
      }
      this.$bvModal.show('work-order-email-modal');
    },
    openSelectedAssignModal() {
      if (!this.hasSelectedWorkOrders) {
        return;
      }
      this.$bvModal.show('work-order-assign-modal');
    },
    openSelectedCompleteModal() {
      if (!this.hasSelectedWorkOrders) {
        return;
      }
      this.$bvModal.show('work-order-complete-modal');
    },
    openSelectedCancelModal() {
      if (!this.hasSelectedWorkOrders) {
        return;
      }
      this.$bvModal.show('work-order-cancel-modal');
    },
    openSelectedUndoCompletionModal() {
      if (!this.hasSelectedWorkOrders) {
        return;
      }
      this.$bvModal.show('work-order-undo-completion-modal');
    },
    clearSelectedWorkOrders() {
      this.selectedWorkOrders = [];
      this.$refs.workOrdersTable?.clearSelection();
    },
  },
  computed: {
    isCMSOrganization() {
      return this.$store.getters.isCMSOrganization;
    },
    hasSelectedWorkOrders() {
      return this.selectedWorkOrders.length > 0;
    },
  }
};
</script>
